import { computed, ComputedRef, readonly, ref, Ref, watch } from 'vue';
import { PostalCodeWithCitiesModel, LocationClientService } from '@/types/locationServerApi';
import utilitiesService from '@/shared/utilities.service';
import { debounce } from 'lodash';
import useCountries from '@/shared/address/useCountries';

export const manuallyTypedCityOptionValue = 'MANUAL';

export function usePostalCodeSuggest() {
    const postalCodeFieldModel: Ref<string> = ref('');
    const cityModel: Ref<string> = ref('');
    const selectedMultiCityOption: Ref<string> = ref('');
    const hasMultipleCities: Ref<boolean> = ref(false);
    const isUnknownPostalCode: Ref<boolean> = ref(false);
    const cities: Ref<string[]> = ref([]);
    const manuallyTypedCity: ComputedRef<boolean> = computed(() => hasMultipleCities.value && selectedMultiCityOption.value === manuallyTypedCityOptionValue);
    const { siteCountryCode } = useCountries();

    const setData = (postalCodeModel: PostalCodeWithCitiesModel | undefined) => {
        isUnknownPostalCode.value = postalCodeModel?.isUnknown ?? true;
        if (postalCodeModel && !postalCodeModel.isUnknown) {
            postalCodeFieldModel.value = postalCodeModel.postalCode ?? '';
            if (postalCodeModel.cities && postalCodeModel.cities.length > 1) {
                hasMultipleCities.value = true;
                cities.value = postalCodeModel.cities ?? [];
                initSelectedMultiCityOption();
            } else {
                cities.value = [];
                hasMultipleCities.value = false;
                cityModel.value = postalCodeModel.cities ? postalCodeModel?.cities[0] : '';
            }
        }
    };

    const initSelectedMultiCityOption = (): void => {
        if (cities.value.indexOf(cityModel.value) > -1) {
            selectedMultiCityOption.value = cityModel.value;
        } else if (cityModel.value) {
            selectedMultiCityOption.value = manuallyTypedCityOptionValue;
        } else {
            selectedMultiCityOption.value = '';
        }
    };

    const fetchCitesAndSetData = async(postalCode: string): Promise<void> => {
        LocationClientService.getCitiesByPostalCode(postalCode, siteCountryCode.value)
            .then(utilitiesService.unwrapModel)
            .then(setData);
    };

    const debouncedFetchCitiesAndSetData = debounce(fetchCitesAndSetData, 200);

    watch(postalCodeFieldModel, () => {
        if (postalCodeFieldModel.value?.length > 3) {
            debouncedFetchCitiesAndSetData(postalCodeFieldModel.value);
        }
    });

    return {
        cities: readonly(cities),
        hasMultipleCities: readonly(hasMultipleCities),
        manuallyTypedCity,
        cityModel,
        selectedMultiCityOption,
        postalCodeFieldModel,
        isUnknownPostalCode,
    };
}
